import { CountBadge } from '@vrplatform/components';
import {
  useDashboard,
  useQuery,
  useRoutePermission,
  useSubscription,
  useTeam,
  useTeamPermission,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { groupBy } from '@vrplatform/utils';
import { Box, Stack, Text } from '@mantine/core';
import { XIMPLIFI_TENANT_ID } from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { useNavbarExpanded } from '../hooks';
import { NavbarRoute } from '../layout/navbar';
import { QuickSearchRoute } from './QuickSearchRoute';
import { PartnerRegionSwitch } from './PartnerRegionSwitch';
import { HiddenFeatureIndicator } from '@vrplatform/embed/components';
import { platformDefaultsSubRoutes } from './platformDefaultsNavigation.routes';

function useLegacyReportViews() {
  const [
    {
      id: teamId,
      hasEntities: { hasPms },
    },
  ] = useTeam();

  const { data } = useQuery(
    (q, args) => {
      const views = q
        .views({
          where: {
            tenantId: { _eq: args.teamId },
            group: { _eq: 'Reports' },
          },
          order_by: [{ sortOrder: 'asc_nulls_last' }],
        })
        .map((view) => ({
          id: view.id,
          title: view.title || '',
          group: view.group,
          retoolViewId: view.retoolViewId,
          sortOrder: view.sortOrder,
        }));

      return {
        views,
      };
    },
    {
      queryKey: 'views',
      skip: !teamId,
      variables: {
        teamId,
      },
    }
  );

  const retoolViews = useMemo(() => {
    const grouped = groupBy(data?.views || [], (x) => x.group!);

    return (
      grouped?.Reports?.map((i) => ({
        id: i.id!,
        title: i.title,
        url: i.retoolViewId!,
        group: i.group!.toLowerCase(),
      })) ?? []
    );
  }, [data?.views]);

  return {
    retoolViews,
    data,
    hasPmsConnection: hasPms,
  };
}

const iconSize = 18;

const NavigationSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const { isDesktopExpanded } = useNavbarExpanded();

  return (
    <Box>
      {title && isDesktopExpanded && (
        <Text
          component="span"
          mb="xs"
          c="white"
          pl={5}
          sx={{ opacity: 0.5, display: 'block' }}
        >
          {title}
        </Text>
      )}
      <Stack gap="xs">{children}</Stack>
    </Box>
  );
};

export function PartnerNavigation() {
  const { retoolViews, data, hasPmsConnection } = useLegacyReportViews();
  // LEGACY
  const [dashboard] = useDashboard();
  const [{ partnerId, id: teamId }] = useTeam();
  const canReadAudit = useTeamPermission('audit:read', {
    legacy: 'teamAdmin',
  });
  const canReadStatements = useRoutePermission('statements');
  const showMappingsV2 =
    partnerId === XIMPLIFI_TENANT_ID && data !== undefined && !hasPmsConnection;

  const reViews =
    retoolViews?.filter(
      (x) => !showMappingsV2 || x.url !== '11ef2837-7612-48db-a7c7-68c621d41efc'
    ) ?? [];

  const isXimplifiTeam = teamId === XIMPLIFI_TENANT_ID;
  const isPartnerDashboard = dashboard === 'partner';
  const hasAdditionalRoutes =
    (isXimplifiTeam && isPartnerDashboard) ||
    (showMappingsV2 && isPartnerDashboard) ||
    (reViews.length > 0 && isPartnerDashboard);

  const additionalRoutes = (
    <>
      {isXimplifiTeam && isPartnerDashboard && (
        <NavbarRoute
          title="Line Master List"
          link="/line-master-list"
          icon={<Icon icon="ListUnorderedIcon" size={iconSize} />}
          notification={<LineMasterIssueBadge />}
        />
      )}

      {/* LEGACY */}
      {showMappingsV2 && isPartnerDashboard && (
        <NavbarRoute
          title="Mappings"
          link="/reports/11ef2837-7612-48db-a7c7-68c621d41efc"
          icon={<Icon icon="FileTextIcon" size={iconSize} />}
        />
      )}
      {reViews.length > 0 && isPartnerDashboard && (
        <NavbarRoute
          title="Reports"
          link={`/${retoolViews[0].group}/${retoolViews[0].url}`}
          icon={<Icon icon="FileTextIcon" size={iconSize} />}
          subRoutes={retoolViews}
        />
      )}
    </>
  );

  return (
    <Stack gap="sm">
      <PartnerRegionSwitch />
      <NavigationSection
        title={!['partner', 'overview'].includes(dashboard) ? 'Legacy' : ''}
      >
        <QuickSearchRoute />

        <NavbarRoute
          title="Overview"
          link={'/dashboard'}
          icon={<Icon icon="Activity2Icon" size={iconSize} />}
        />
        {isPartnerDashboard && (
          <>
            <NavbarRoute
              title="Your Team"
              link={'/members'}
              icon={<Icon icon="UsersIcon" size={iconSize} />}
            />
            <NavbarRoute
              title="Partner Billing"
              link={'/billing'}
              icon={<Icon icon="CreditCardIncomeIcon" size={iconSize} />}
            />
            <NavbarRoute
              title="Connections"
              link={'/team-connections?status=active'}
              icon={<Icon icon="ShareIcon" size={iconSize} />}
            />
            {canReadStatements && (
              <NavbarRoute
                title="Statements"
                link={'/statements'}
                icon={<Icon icon="ClipboardTextIcon" size={iconSize} />}
              />
            )}
            {canReadAudit && (
              <NavbarRoute
                title="Activity"
                link="/activity"
                icon={<Icon icon="HistoryIcon" size={iconSize} />}
              />
            )}
            <NavbarRoute
              title="Settings"
              link="/settings"
              icon={<Icon icon="GearIcon" size={iconSize} />}
            />
          </>
        )}

        {!isPartnerDashboard && (
          <>
            <Box my="sm">
              <NavbarRoute
                title="Statements"
                link={'/team-statements'}
                icon={<Icon icon="ClipboardTextIcon" size={iconSize} />}
              />
            </Box>
            <Stack gap="xs" mb="sm">
              <NavbarRoute
                title="Connections"
                link={'/team-connections?status=active'}
                icon={<Icon icon="ShareIcon" size={iconSize} />}
              />
            </Stack>
            {additionalRoutes}
          </>
        )}
      </NavigationSection>

      {isPartnerDashboard && hasAdditionalRoutes && (
        <NavigationSection title="">{additionalRoutes}</NavigationSection>
      )}

      {isPartnerDashboard && (
        <HiddenFeatureIndicator permission="vrp-admin" p={6}>
          <NavigationSection title="Partner Defaults">
            <NavbarRoute
              title="Centralized Defaults"
              link="/platform-defaults/general-ledger"
              icon={<Icon icon="GearIcon" size={iconSize} />}
              subRoutes={platformDefaultsSubRoutes}
            />
          </NavigationSection>
        </HiddenFeatureIndicator>
      )}
    </Stack>
  );
}
const LineMasterIssueBadge = () => {
  const [{ name: partnerName }] = useTeam();

  const { data, isLoading } = useSubscription(
    (q) => {
      const count =
        q
          .paymentLineClassificationAggregate({
            where: {
              _not: {
                settings: {
                  value: { _is_null: false },
                  key: { _eq: 'accountingType' },
                  parentSettingId: { _is_null: true },
                },
              },
            },
          })
          .aggregate?.count() || 0;

      return count;
    },
    { partnerName: partnerName.toLowerCase() }
  );

  return (
    <CountBadge count={data || 0} loading={isLoading} loadingColor="white" />
  );
};
